import { useState } from "react";
import { AlertTriangle, Loader2, Plus, ShieldAlert, Star, Trash2, Truck, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  useAdminSuppliers,
  useCreateSupplier,
  useDeleteSupplier,
  useUpdateSupplier,
} from "@/hooks/useSuppliers";
import type {
  AdminSupplierOptions,
  AdminSupplierPayload,
  AdminSupplierRow,
  SupplierBrandPayload,
} from "@/api/suppliers";

/**
 * D-133 Фаза A (A-W3) — Адмін-довідник постачальників (CSupplier CRUD).
 * Доступ: admin OR QueueAdmin (permissions.canManageSuppliers — backend SoT).
 * Бренд-маппінги (CSupplierBrand: role + default) редагуються тут же.
 */

const LANG_LABEL: Record<string, string> = { uk: "українська", en: "англійська" };
const ROLE_LABEL: Record<string, string> = {
  manufacturer: "виробник",
  distributor: "дистрибʼютор",
  importer: "імпортер",
};

export function AdminSuppliersPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canManage = !!perms?.canManageSuppliers;

  const q = useAdminSuppliers(canManage);
  const [editId, setEditId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  if (!canManage) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до довідника постачальників</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Розділ доступний адміністраторам та керівникам черги рекламацій.
          </p>
        </div>
      </div>
    );
  }

  const data = q.data;
  const editing = editId ? data?.items.find((i) => i.id === editId) ?? null : null;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px]">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-lg font-semibold">Постачальники</h1>
              <p className="text-xs text-muted-foreground">
                Довідник постачальників · бренди, контакти, мова листування (для відправки рекламацій)
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> Додати
          </Button>
        </div>

        <div className="rounded-lg border bg-card">
          {q.isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Завантаження…
            </div>
          ) : q.isError ? (
            <div className="p-10 text-center text-sm text-destructive">Помилка завантаження.</div>
          ) : !data?.items.length ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Постачальників ще немає. Натисніть «Додати».
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Назва</th>
                  <th className="px-2 py-2.5 font-medium">Email</th>
                  <th className="px-2 py-2.5 font-medium">Бренди</th>
                  <th className="px-2 py-2.5 font-medium">Мова</th>
                  <th className="px-2 py-2.5 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setEditId(s.id)}
                    className={cn(
                      "cursor-pointer border-t transition-colors hover:bg-accent/50",
                      !s.active && "opacity-60",
                    )}
                  >
                    <td className="px-4 py-2.5 font-medium">{s.name}</td>
                    <td className="px-2 py-2.5 text-muted-foreground">{s.email ?? "—"}</td>
                    <td className="px-2 py-2.5">
                      {s.brands.length ? (
                        <div className="flex flex-wrap gap-1">
                          {s.brands.map((b) => (
                            <span
                              key={b.id}
                              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                              title={ROLE_LABEL[b.role] ?? b.role}
                            >
                              {b.isDefault && <Star className="h-3 w-3 fill-amber-400 text-amber-400" />}
                              {b.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">{LANG_LABEL[s.language] ?? s.language}</td>
                    <td className="px-2 py-2.5">
                      {s.active ? (
                        <Badge variant="success">активний</Badge>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[12px] font-medium text-muted-foreground">
                          неактивний
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(editing || creating) && data && (
        <SupplierDrawer
          supplier={editing}
          options={data.options}
          onClose={() => {
            setEditId(null);
            setCreating(false);
          }}
          onToast={showToast}
        />
      )}

      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok" ? "bg-success text-success-foreground" : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Drawer (create / edit) ──────────────────────────────────────────────────

interface BrandRow {
  brandBiId: string;
  role: string;
  isDefault: boolean;
  /** Known name of the mapped brand — keeps an already-mapped (possibly deactivated) brand
   *  selectable even when it's absent from the active-only options list. */
  name?: string;
}

function SupplierDrawer({
  supplier,
  options,
  onClose,
  onToast,
}: {
  supplier: AdminSupplierRow | null;
  options: AdminSupplierOptions;
  onClose: () => void;
  onToast: (kind: "ok" | "err", msg: string) => void;
}) {
  const isCreate = !supplier;
  const panelRef = useDrawerA11y(true, onClose);
  const createM = useCreateSupplier();
  const updateM = useUpdateSupplier();
  const deleteM = useDeleteSupplier();

  const [name, setName] = useState(supplier?.name ?? "");
  const [email, setEmail] = useState(supplier?.email ?? "");
  const [additionalEmails, setAdditionalEmails] = useState((supplier?.additionalEmails ?? []).join("\n"));
  const [qualityName, setQualityName] = useState(supplier?.qualityContactName ?? "");
  const [qualityEmail, setQualityEmail] = useState(supplier?.qualityContactEmail ?? "");
  const [salesName, setSalesName] = useState(supplier?.salesContactName ?? "");
  const [salesEmail, setSalesEmail] = useState(supplier?.salesContactEmail ?? "");
  const [language, setLanguage] = useState(supplier?.language ?? "uk");
  const [active, setActive] = useState(supplier?.active ?? true);
  const [notes, setNotes] = useState(supplier?.notes ?? "");
  const [brands, setBrands] = useState<BrandRow[]>(
    (supplier?.brands ?? []).map((b) => ({
      brandBiId: b.biBrandId ?? "",
      role: b.role,
      isDefault: b.isDefault,
      name: b.name,
    })),
  );

  const pending = createM.isPending || updateM.isPending || deleteM.isPending;

  const submit = () => {
    if (!name.trim()) {
      onToast("err", "Назва — обовʼязкова.");
      return;
    }
    const cleanBrands: SupplierBrandPayload[] = brands
      .filter((b) => b.brandBiId)
      .map((b) => ({ brandBiId: b.brandBiId, role: b.role, isDefault: b.isDefault }));

    const body: AdminSupplierPayload = {
      name: name.trim(),
      email: email.trim() || null,
      additionalEmails: additionalEmails
        .split(/[\n,;]+/)
        .map((e) => e.trim())
        .filter(Boolean),
      qualityContactName: qualityName.trim() || null,
      qualityContactEmail: qualityEmail.trim() || null,
      salesContactName: salesName.trim() || null,
      salesContactEmail: salesEmail.trim() || null,
      language,
      active,
      notes: notes.trim() || null,
      supplierBrands: cleanBrands,
    };
    const onResult = {
      onSuccess: () => {
        onToast("ok", isCreate ? "Постачальника створено" : "Збережено");
        onClose();
      },
      onError: (e: unknown) => onToast("err", e instanceof Error ? e.message : "Помилка"),
    };
    if (isCreate) createM.mutate(body, onResult);
    else updateM.mutate({ id: supplier!.id, body }, onResult);
  };

  const onDelete = () => {
    if (!supplier) return;
    if (!window.confirm(`Видалити постачальника «${supplier.name}»? Маппінги брендів буде знято.`)) return;
    deleteM.mutate(supplier.id, {
      onSuccess: () => {
        onToast("ok", "Постачальника видалено");
        onClose();
      },
      onError: (e: unknown) => onToast("err", e instanceof Error ? e.message : "Помилка"),
    });
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={isCreate ? "Новий постачальник" : `Постачальник ${supplier?.name}`}
        className="absolute right-0 top-0 h-full w-[460px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{isCreate ? "Новий постачальник" : supplier?.name}</h2>
          <button onClick={onClose} aria-label="Закрити" className="rounded p-1 hover:bg-accent">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Назва *">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Основний email">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="claims@vendor.com" />
            </Field>
            <Field label="Мова листування">
              <Select
                value={language}
                onChange={setLanguage}
                options={options.languages.map((l) => ({ value: l, label: LANG_LABEL[l] ?? l }))}
              />
            </Field>
          </div>
          <Field label="Додаткові email (по одному в рядок)">
            <textarea
              value={additionalEmails}
              onChange={(e) => setAdditionalEmails(e.target.value)}
              placeholder="cc@vendor.com"
              className="h-16 w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Контакт з якості (імʼя)">
              <Input value={qualityName} onChange={(e) => setQualityName(e.target.value)} />
            </Field>
            <Field label="Контакт з якості (email)">
              <Input value={qualityEmail} onChange={(e) => setQualityEmail(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Контакт продажів (імʼя)">
              <Input value={salesName} onChange={(e) => setSalesName(e.target.value)} />
            </Field>
            <Field label="Контакт продажів (email)">
              <Input value={salesEmail} onChange={(e) => setSalesEmail(e.target.value)} />
            </Field>
          </div>

          <Field label="Статус">
            <Select
              value={active ? "1" : "0"}
              onChange={(v) => setActive(v === "1")}
              options={[
                { value: "1", label: "активний" },
                { value: "0", label: "неактивний" },
              ]}
            />
          </Field>

          <BrandMappingEditor value={brands} onChange={setBrands} options={options} />

          <Field label="Нотатки (внутрішні — не видно менеджеру)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-16 w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={pending}>
            Скасувати
          </Button>
          <Button size="sm" onClick={submit} disabled={pending}>
            {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isCreate ? "Створити" : "Зберегти"}
          </Button>
          {!isCreate && (
            <button
              onClick={onDelete}
              disabled={pending}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Видалити
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Brand-mapping editor (CSupplierBrand: role + default) ────────────────────

function BrandMappingEditor({
  value,
  onChange,
  options,
}: {
  value: BrandRow[];
  onChange: (rows: BrandRow[]) => void;
  options: AdminSupplierOptions;
}) {
  const setRow = (i: number, patch: Partial<BrandRow>) =>
    onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const addRow = () =>
    onChange([...value, { brandBiId: "", role: options.roles[1] ?? options.roles[0] ?? "distributor", isDefault: false }]);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="block text-xs font-medium">Бренди постачальника</label>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-primary hover:bg-primary/10"
        >
          <Plus className="h-3.5 w-3.5" /> Додати бренд
        </button>
      </div>
      {value.length === 0 ? (
        <p className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          Бренди не призначено. Авто-пропозиція по бренду рекламації не спрацює без маппінгу.
        </p>
      ) : (
        <div className="space-y-1.5">
          {value.map((row, i) => (
            <div key={i} className="flex items-center gap-1.5 rounded-md border bg-background p-1.5">
              <select
                value={row.brandBiId}
                onChange={(e) => setRow(i, { brandBiId: e.target.value })}
                className="h-8 min-w-0 flex-1 rounded border bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— бренд —</option>
                {/* already-mapped brand that's no longer active → keep it selectable */}
                {row.brandBiId && !options.brands.some((b) => b.biBrandId === row.brandBiId) && (
                  <option value={row.brandBiId}>{(row.name || row.brandBiId) + " (неактивний)"}</option>
                )}
                {options.brands.map((b) => (
                  <option key={b.biBrandId} value={b.biBrandId}>
                    {b.name}
                  </option>
                ))}
              </select>
              <select
                value={row.role}
                onChange={(e) => setRow(i, { role: e.target.value })}
                className="h-8 w-28 rounded border bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {options.roles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r] ?? r}
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-pressed={row.isDefault}
                title={row.isDefault ? "Постачальник за замовчуванням" : "Зробити за замовчуванням"}
                onClick={() => setRow(i, { isDefault: !row.isDefault })}
                className={cn(
                  "rounded p-1.5 transition-colors",
                  row.isDefault ? "text-amber-500" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Star className={cn("h-4 w-4", row.isDefault && "fill-amber-400")} />
              </button>
              <button
                type="button"
                aria-label="Прибрати бренд"
                onClick={() => removeRow(i)}
                className="rounded p-1.5 text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="mt-1 flex items-start gap-1 text-[11px] text-muted-foreground">
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
        <span>★ — постачальник за замовчуванням для бренду (один на бренд; обирається авто-пропозицією).</span>
      </p>
    </div>
  );
}

// ─── small form helpers (mirrors AdminLegalEntitiesPage) ──────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
